import React, { useEffect } from "react";
import styles from "./Result.scss";
import cx from "../../utils/cx";
import { IFinancialCheckData } from "./FinancialCheckPage";
import Translate from "../../utils/Translate";
import Tooltip from "../Tooltip";
import DonutChart from "./DonutChart";
import BarChart from "./BarChart";
import Icon from "../MaterialUi/Icon";
import Responsive from "../../utils/Responsive";
import LoadingIndicator from "../LoadingIndicator";
import Checkbox from "../MaterialUi/Checkbox";
import createToast from "../MaterialUi/Toast";
import LeadSmartApi, { ILeadData, IMortageData } from "../../utils/LeadSmartApi";
import Button from "../MaterialUi/Button";
import LogError from "../../utils/LogError";
import ReactDOMServer from "react-dom/server";
import JsonApiError from "../JsonApiError";
import { DEFAULT_PRICE, IBaufiImplecoLeadPageProps, IPersonFields } from "./BaufiImplecoLeadPage";
import TextField from "../MaterialUi/TextField";
import ErrorBox from "../RegisterEstateAgent/ErrorBox";
import { inputPatterns } from "../../utils/inputPatterns";

export interface IResultProps {
    className?: string;
    nextStep: () => void;
    setData: (data?: IFinancialCheckData) => void;
    data: IFinancialCheckData;
    prevStep?: () => void;
    variant?: IBaufiImplecoLeadPageProps['variant'];
}

const Result: React.FC<IResultProps> = ({ prevStep, nextStep, data, setData, className = '' }) => {

    const [termsData, setTermsData] = React.useState(data.termsData || {} as IFinancialCheckData['termsData']);
    const [fields, setFields] = React.useState<IPersonFields>(data.firstPerson as IPersonFields || {} as IPersonFields);
    
    // const [termsEmail, setTermsEmail] = React.useState(data?.termsData?.email);
    const [isLoading, setIsLoading] = React.useState(true);
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const [error, setError] = React.useState<string>(null);

    const legalTermsNode = <>
        Ich stimme den Homecloud  <a href="https://www.homecloud.de/agb" target="_blank">AGB</a> zu
        und möchte eine persönliche Beratung zu meinem Finanzierungsvorhaben erhalten. Ich stimme der Übermittlung meiner Daten und der Kontaktierung per E-Mail
        durch <a href={data.campaign?.companyWebsite} target="_blank" className={styles.termsPlaceholder}>
            <span className={data.campaign?.campaignId ? null : styles.empty}>{data.campaign?.companyName || ''}</span>
        </a> zu.
    </>

    const privacyTermsNode = <>
        Bitte beachte die
        {' '}<a href={data.campaign?.dataPrivacyUrl} target="_blank" className={styles.termsPlaceholder}>Datenschutzhinweise von {data.campaign?.companyName || ''}</a>,
        die
        {' '}<a target="_blank" href="https://www.homecloud.de/datenschutz">Datenschutzhinweise von Homecloud</a> und unsere
        {' '}<a target="_blank" href="/info/datenschutz">Datenschutzhinweise von Wohnglück.de</a>.
    </>


    useEffect(() => {

        if (!data?.firstPerson?.postalCode) {
            createToast('Bitte gib eine Postleitzahl an.', 'error');
            setIsLoading(false);
            return;
        }

        setIsLoading(true);

        // setIsSubmitting(true);
        // create preflight to get campaign and get mortgage data  
        LeadSmartApi.leadPreflight(data.firstPerson.postalCode).then((res) => {

            if (!res.leadConsumers[0]) {
                createToast('Der Finanzierungsrechner ist momentan nicht verfügbar.', 'error');
                LogError.add(new Error('LeadSmart preflight returned no leadConsumer'), {
                    title: 'LeadSmart preflight returned no leadConsumer',
                    error_code: 'FinancialCheck.Result.tsx:leadPreflight:noLeadConsumers',
                    request_data: { postalCode: data.firstPerson.postalCode },
                });
                return;
            }

            setData({ campaign: res.leadConsumers[0] });


            const mortgageData: IMortageData = {
                firstPerson: {
                    "additionalMonthlyIncome": data.firstPerson.additionalMonthlyIncome,
                    "birthday": data.firstPerson.birthday,
                    "employedSince": data.firstPerson.employedSince,
                    "monthlyNetIncomeInEuro": data.firstPerson.monthlyNetIncomeInEuro,
                },
                household: {
                    equityTotalInEuro: data.loanData.equity,
                },
                purchasePriceInEuro: Number(data.property?.price || DEFAULT_PRICE),
                subtype: 'PURCHASE'
            };
            LeadSmartApi.getMortgage(mortgageData)
                .then((res) => {
                    setData({ mortgageResponse: res });
                })
                .catch((e) => {
                    console.error(e);
                    createToast(`Fehler beim Berechnen der Finanzierungskonditionen. ${e.responseData?.message}`, 'error');
                    LogError.add(e, {
                        title: 'Error on getting mortgage data with LeadSmartApi',
                        error_code: 'FinancialCheck.Result.tsx:getMortgage',
                        request_data: { mortgageData, apiMessage: e.responseData?.message },
                    });
                    if (e.responseData?.message) {
                        setError(e.responseData.message);
                    }
                })
                .finally(() => {
                    // setIsSubmitting(false);
                    setIsLoading(false);
                });


            // setData({ loanData: fields, campaign: res.leadConsumers[0] });
        }).catch((e) => {
            console.error(e);
            createToast(`Der Finanzierungsrechner ist für diese Postleitzahl nicht verfügbar. ${e.responseData?.message}`, 'error');
            LogError.add(e, {
                title: 'Error on preflight with LeadSmartApi',
                error_code: 'FinancialCheck.Result.tsx:leadPreflight',
                request_data: { postalCode: data.firstPerson.postalCode, apiMessage: e.responseData?.message },
            });
            if (e.responseData?.message) {
                setError(e.responseData.message);
            }
        }).finally(() => {
            setIsSubmitting(false);
        });


    }, []);

    const handlePrevStep = (ev) => {
        prevStep();
    }


    const handleChange = (ev) => {
        const name = ev.target.name as keyof IPersonFields;
        const value = ev.target.value;
        setFields({
            ...fields,
            [name]: value
        });
    }
    
    // const handleTermsChange = (ev) => {
    // // setData({ termsData: { ...data.termsData, [ev.target.name]: ev.target.checked } });
    //     setTermsEmail(ev.target.checked);
    // }
    const handleTermsChange = (ev) => {
        const name = ev.target.name as keyof ILeadData['firstPerson'];
        const value = ev.target.checked;
        setTermsData({
            ...termsData,
            [name]: value
        });
    }

    const handleSubmit = (ev: React.SyntheticEvent) => {
        ev.preventDefault();
        // if (!termsEmail) {
        //     createToast('Bitte akzeptiere die Datenschutzbestimmungen', 'warning');
        //     return;
        // }
        // if (!data?.leadResponse?.id) {
        //     createToast('Missing: leadId', 'error');
        //     return;

        // }

        console.log('###handleSubmit', data);

        if (!data?.campaign?.campaignId) {
            createToast('Ein Fehler ist aufgetreten: campaignId fehlt', 'error');
            LogError.add(new Error('Missing LeadSmart campaignId'), {
                title: 'Missing LeadSmart campaignId',
                error_code: 'FinancialCheck.Result.tsx:handleSubmit:campaignId',
                request_data: { campaignId: data.campaign?.campaignId },
            });
            return;
        }

        setIsSubmitting(true);

        const legalTerms = ReactDOMServer.renderToString(legalTermsNode);
        const privacyText = ReactDOMServer.renderToString(privacyTermsNode);


        const createLeadData = {
            // "chosenLeadCampaignId": data.campaign?.campaignId,
            // "campaign": "SuperLeads-Landingpage-BaufiVergleich",
            // "keyword": "SuperLeads-Passt-JetztFinanzierungsvergleichStarten",
            // "dataContext": "TEST",
            // "source": "SuperLeads",
            "trackingId": `WGL-${(new Date()).toISOString()}`,
            "type": "MORTGAGE_LEAD" as const,
            "subtype": "PURCHASE" as const,
            // "notes": "Erreichbar: 10-14 Uhr",
            "firstPerson": {
                "firstName": fields.firstName,
                "lastName": fields.lastName,
                "email": fields.email,
                // "phoneNumber": "0172 123 456",
                "birthday": data.firstPerson.birthday,
                // "civilStatus": "MARRIED",
                "employedSince": data.firstPerson.employedSince,
                "monthlyNetIncomeInEuro": data.firstPerson.monthlyNetIncomeInEuro,
                "additionalMonthlyIncome": data.firstPerson.additionalMonthlyIncome,
                "employmentType": LeadSmartApi.typeOfEmployment2employmentType(data.firstPerson.typeOfEmployment),
                // "profession": "Software Developer",
                "address": {
                    // "street": "Heidestr.",
                    // "houseNumber": "8",
                    "postalCode": data.firstPerson.postalCode,
                    // "city": "Berlin",
                    // "state": "Berlin"
                }
            },
            "secondPerson": undefined,
            "household": {
                "remainingDebtTotalInEuro": data.loanData.remainingDebt,
                "equityTotalInEuro": data.loanData.equity,
            },
            "partialHomeInformation": {
                "plotAvailable": data.property?.plot_area && Number(data.property?.plot_area) > 0 ? true : false,
                "postalCode": data.property?.field_address?.postal_code,
                "type": LeadSmartApi.propertyType2PartialHomeInformationType(data.property?.property_type_name),
                // "usageType": "OWN_USE" // TODO: use API specific keywords when API documentation is available
            },
            "mortgage": {
                "calculations": {
                    "purchasePriceInEuro": data.loanData.price,
                    "mortgageAmountInEuro": data.loanData.loan
                },
                // "projectStage": "DECIDED",
                // "projectTimeline": "IMMEDIATELY"
            },
            "legal": {
                "userConsentType": "CHECKBOX" as const,
                "userAuthenticationType": "NONE" as const,
                "contactByEmailConsentWording": legalTerms,
                "isContactByEmailWanted": true,
                "isContactByPhoneWanted": false, // keine Tel. angegeben
                "mortgageAdvisoryWantedWording": legalTerms,
                "isMortgageAdvisoryWanted": true,
                "mortgageAdvisorDataForwardingWording": legalTerms,
                "isMortgageAdvisorDataForwardingChecked": true,
                "realEstateDataForwardingWording": legalTerms,
                "isRealEstateDataForwardingChecked": true,
                "partnerDataPrivacyNotesWording": privacyText,
                "hasReceivedPartnerDataPrivacyNotes": true,
                "homecloudDataPrivacyNotesWording": privacyText,
                "hasReceivedHomecloudDataPrivacyNotes": true,
                "homecloudTermsWording": legalTerms,
                "isHomecloudTermsChecked": true,
            }
        }
        LeadSmartApi.createLead(createLeadData).then((leadResponse) => {
            // setData({ leadResponse, termsData: { ...data.termsData, email: termsEmail } });
            setData({ leadResponse, termsData: { ...data.termsData }, firstPerson: fields });
            nextStep();
        }).catch(e => {
            console.error(e);
            LogError.add(e, {
                title: 'Error on creating lead with LeadSmartApi',
                error_code: 'FinancialCheck.Result.tsx:createLead',
                request_data: { leadData: createLeadData, apiMessage: e.responseData?.message },
            });
            // createToast(`Fehler beim Abruf der API: ${e || 'unbekannter Fehler'} ${(e?.responseData?.message || '')}`, 'error');
            createToast(<JsonApiError error={e} text="Fehler beim Abruf der API" />, 'error');
        }
        ).finally(() => {
            setIsSubmitting(false);
        });
    }

    const score = data?.mortgageResponse?.scoring?.feasibilityScoreAsGrade?.toLowerCase();
    const isGreen = score === 'a' || score === 'b';
    // const canContact = data?.termsData?.email;

    const isSubmitButtonEnabled = data?.mortgageResponse && termsData.homecloud && data?.campaign?.campaignId && fields.firstName && fields.lastName && fields.email;

    return <div className={cx(styles.financialCheckResult, className)}>
        <form onSubmit={handleSubmit}>
            <h1>Dein Ergebnis:</h1>

            {isLoading
                ? <div className={styles.scoreLoadingIndicator}><LoadingIndicator size="medium" /> Wir berechnen dein Ergebnis, dies kann einen Augenblick dauern.</div>
                : <div className={cx(styles.score, isGreen ? styles.green : null)}>
                    {isGreen
                            ? <><Icon name="thumbUp" />Finanzierung wahrscheinlich möglich!</>
                            : <>Weitere Prüfung für deine Finanzierung erforderlich!</>
                        }
                </div>

            }

            {error && <ErrorBox>{error}</ErrorBox>}

            <p>
                Im nächsten Schritt kannst du eine persönliche Beratung
                beantragen. Wir haben dir bereits einen passenden
                Baufinanzierungsberater ermittelt, an den wir dich gerne
                weiterleiten.
            </p>
            <div className={styles.conditions}>
                <div>
                    Monatliche Rate
                    {' '}
                    <Tooltip>
                        Die ermittelte monatliche Rate basiert auf aktuell gültigen Konditionen und bezeichnet die monatliche Zahlung die geleistet werden muss.
                        Die Berechnung basiert auf den zuvor angegebenen persönlichen Informationen sowie den vorliegenden Informationen zur Immobilie.
                        In der Rate sind sowohl die Tilgung des Darlehens als auch die anfallenden Zinsen enthalten.<br />
                        <br />
                        Die finalen Konditionen werden im Rahmen der persönlichen Finanzierungsberatung ermittelt.<br />
                        <br />
                        Details der ermittelten Konditionen:<br />
                        <br />
                        Effektiver Jahreszins: {Translate.getNumber(data?.mortgageResponse?.displayConditions?.effectiveInterestRateInPercent)}%<br />
                        Gebundener Sollzins: {Translate.getNumber(data?.mortgageResponse?.displayConditions?.fixedInterestRateInPercent)}%<br />
                        Sollzinsbindung: {Translate.getNumber(data?.mortgageResponse?.displayConditions?.fixedInterestPeriodInYears)} Jahre<br />
                        {data?.mortgageResponse?.displayConditions?.offeringBank && <>Bank: {data?.mortgageResponse?.displayConditions?.offeringBank}<br /></>}
                    </Tooltip>
                </div>
                <div className={styles.rate}>
                    {
                        isLoading
                            ? <LoadingIndicator size="medium" />
                            : Translate.getPrice(data?.mortgageResponse?.displayConditions?.monthlyPaymentInEuro || 0)
                    }
                </div>
            </div>

            <div className={styles.charts}>
                <DonutChart loanData={data.loanData} />
            </div>
            <div className={styles.charts}>
                <BarChart loanData={data.loanData} />
            </div>

            <div className={styles.firstPersonData}>
                <h4>Kontaktdaten für den Finanzierungsberater</h4>
                <div className={styles.firstName}>
                    <TextField name="firstName" value={fields.firstName || ''} onChange={handleChange} label="Vorname" required autoComplete="given-name"/>
                    <Tooltip>
                        Diese Information dient dazu, dass dich dein Finanzierungsberater und ggf. die Immobilien-Anbieter kontaktieren können.
                    </Tooltip>
                </div>
                <div className={styles.lastName}>
                    <TextField name="lastName" value={fields.lastName || ''} onChange={handleChange} label="Nachname" required autoComplete="family-name" />
                    <Tooltip>
                        Diese Information dient dazu, dass dich dein Finanzierungsberater und ggf. die Immobilien-Anbieter kontaktieren können.
                    </Tooltip>
                </div>
                <div>
                    <TextField 
                        name="email" 
                        value={fields.email || ''} 
                        type="email" 
                        onChange={handleChange} 
                        label="E-Mail Adresse" 
                        required  
                        autoComplete="email"
                        onBlurError={fields.email && !inputPatterns.test(fields.email, 'email')}
                    />
                    <Tooltip>
                        Diese Information dient dazu, dass dich dein Finanzierungsberater und ggf. die Immobilien-Anbieter kontaktieren können.
                    </Tooltip>
                </div>

            </div>


            <div className={styles.additionalInfo}>


                <div className={styles.terms}>
                    <Checkbox
                        required
                        value={termsData.homecloud}
                        name="homecloud"
                        onChange={handleTermsChange}
                        label={legalTermsNode}
                    />
                    {/* <Checkbox
                            value={termsData.email}
                            name="email"
                            onChange={handleTermsChange}
                            label={legalTextEmail}
                        />
                        <p>
                            Die Datenschutzhinweise unseres Finanzierungspartners findest du <a href={data?.affiliate?.privacy_uri} target="_blank" >hier</a> und
                            die Datenschutzhinweise von Wohnglück <a target="_blank" href="/info/datenschutz">hier</a>.
                        </p> */}
                </div>




                {/* <p className={styles.terms}>
                    Die Datenschutzhinweise unseres Finanzierungspartners findest du <a href={data?.affiliate?.privacy_uri} target="_blank" >hier</a> und
                    die Datenschutzhinweise von Wohnglück <a target="_blank" href="/info/datenschutz">hier</a>.
                </p> */}
                {/* <Checkbox
                    required
                    value={termsEmail}
                    name="email"
                    onChange={handleTermsChange}
                    label={legalTextEmail}
                /> */}
            </div>


            <div className={styles.actions}>
                {Responsive.isSmartphone()
                    ? <Button variant="outlined" color="inherit" onClick={handlePrevStep} startIcon={<Icon name="navigatePrev" />}></Button>
                    : <Button variant="outlined" color="inherit" onClick={handlePrevStep} startIcon={<Icon name="navigatePrev" />}>zum letzten Schritt zurück</Button>
                }

                {/* {canContact
                    ? <Button variant="text" href={`/property/${data.property?.uid}`} endIcon={<Icon name="navigatePrev" />}>zum Exposé zurück</Button>
                    : <Button loading={isLoading} disabled={!data?.mortgageResponse} endIcon={<Icon name="navigateNext" />}>Finanzierungsberatung anfragen</Button>
                } */}
                <Button
                    loading={isSubmitting}
                    visualDisabled={!isSubmitButtonEnabled}
                    endIcon={<Icon name="navigateNext" />}
                    type="submit"
                // onClick={handleSubmit}
                >Finanzierungsberatung anfragen</Button>

            </div>

            <p className={styles.terms}>
                {privacyTermsNode}
            </p>

        </form>

    </div >
};

export default Result;
